import { shipToRecords } from '../../utils/shipToLocation';
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table, Modal } from 'react-bootstrap';
import { updateJob } from '../../redux/slices/jobScheduleSlice';
import EditJobModal from './EditJobModal';
import NewJobModal from './NewJobModal';
import SaveBanner from '../JobDetailPage/Mechanical/SaveBanner';
import useScheduleChangeLog from '../../functions/useScheduleChangeLog';
import useWeekCalculations from '../../functions/useWeekCalculations';
import { mapUserRoles, useRoleMapping } from '../../functions/useRoleMapping';
import moment from 'moment';
import MOSearchModal from './MOSearchModal';
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import { selectSchedulePumpDetails, selectChangeOrderDetails } from "../../redux/selectors";  // Change import
import './tableStyles.css';

// Import new components
import JobScheduleTableHeader from './components/JobScheduleTableHeader';
import JobScheduleControls from './components/JobScheduleControls';
import JobScheduleRow from './components/JobScheduleRow';
import CompactJobScheduleWeek from './components/CompactJobScheduleWeek';
import AlertsModal from './AlertsModal'; // Add AlertsModal import
import AccountingModal from './AccountingModal';

// Import utility functions
import { 
  formatCurrency, 
} from './utils/tableUtils';

// Import highlighting utilities
import {
  hasTbd as checkHasTbd,
  getStationType
} from './utils/highlightingUtils';
import { isSoftwareViewJob } from './utils/softwareViewUtils';
import { isControlPanelViewJob } from './utils/controlPanelViewUtils';
import { isNorthPrairieViewJob } from './utils/northPrairieViewUtils';
import { getJobTypeFromJobNumber } from './utils/jobTypeUtils';
import { isInternationalShippingJob } from './utils/internationalShippingUtils';
import { selectSellPriceData } from '../../redux/slices/sellPriceSlice';

// Import schedule permissions
import {
  hasAnyEditPermission,
  canViewActionColumn
} from './utils/schedulePermissions';

// Import job helpers - removed hasMoveRequest and hasBeenMovedUp from highlightingUtils
import { 
  calculateWeekTotalDollarValue,
  getWeekDateRange,
  hasIncompleteChangeOrderTasks
} from './utils/jobHelpers';

// Import notice utilities
import { getJobNoticeData } from './utils/noticeUtils';

// Import data processing
import { createDataProcessingFunctions } from './utils/dataProcessing';

// Import modal handlers
import {
  createEditJobHandlers,
  createNewJobHandlers,
  createMOSearchHandlers,
  createModalCloseHandler
} from './utils/modalHandlers';

// Import cell edit handlers
import { createCellEditHandlers } from './utils/cellEditHandlers';

// Import export handlers (legacy)

// Import enhanced export handlers
import { 
  exportToPDF, 
  exportToExcelWithFormatting, 
  printTableOptimized 
} from './utils/enhancedExportHandlers';

// Import enhanced export modal
import EnhancedExportModal from './EnhancedExportModal';

const normalizeMultiSelectFilter = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (!value || value === 'all') {
    return [];
  }

  return [value];
};

const expandLegacyStationTypeValues = (values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }

  if (!values.includes('sky-harvester')) {
    return values;
  }

  return [
    ...values.filter((value) => value !== 'sky-harvester'),
    'sky-harvester-micro',
    'sky-harvester-sh80',
    'sky-harvester-custom'
  ];
};

// The main component
const EnhancedJobScheduleTable = ({ canEdit = false, filteredJobs = null }) => {
  const dispatch = useDispatch();
  // Add defensive selectors to prevent undefined errors
  const jobScheduleData = useSelector((state) => state.jobSchedule?.data || []);
  const loading = useSelector((state) => state.jobSchedule?.loading || false);
  const error = useSelector((state) => state.jobSchedule?.error || null);
  const apiUrls = useSelector(selectApiUrls);  const userRoles = useSelector((state) => state.user.userRoles);
  const user = useSelector((state) => state.user || {});
  
  // Always call useRoleMapping (React hook rule)
  const computedUserRoles = useRoleMapping(user.userRoles || [], user.userDetails?.email);
  
  // Use existing rolesMapped if available, otherwise use computed roles
  const mappedUserRoles = user.rolesMapped || computedUserRoles;
  const pumpDetails = useSelector(selectSchedulePumpDetails);  // Use the new selector
  const changeOrderDetails = useSelector(selectChangeOrderDetails);  // Get change order details
  const soldToShipToByProject = useSelector(selectSellPriceData);
  
  const [roles, setRoles] = useState({});
  
  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  // Role-based field permissions still apply, but tab-level policy can globally disable editing.
  const hasEditPermission = Boolean(canEdit) && hasAnyEditPermission(mappedUserRoles);
  // Only show action column when both role and policy allow editing.
  const showActionColumn = Boolean(canEdit) && canViewActionColumn(mappedUserRoles);
  
  // Get TBD data from Redux store
  const tbdList = useSelector((state) => state.tbdApi.tbdList);
    // Function to check if a job has any TBDs
  const hasTbd = useCallback((jobId) => {
    return checkHasTbd(jobId, tbdList);
  }, [tbdList]);
  
  // Function to check if a job has incomplete change order tasks
  const hasIncompleteTasks = useCallback((jobId) => {
    return hasIncompleteChangeOrderTasks(jobId, changeOrderDetails);
  }, [changeOrderDetails]);
  
  // Get user dropdowns from Redux store for engineer selectors
  const userDropdowns = useSelector((state) => state.dropdown.userDropdowns);
  const mechOptions = userDropdowns.mechOptions || [];
  const elecOptions = userDropdowns.elecOptions || [];
  const controlOptions = userDropdowns.controlOptions || [];
  const salesOptions = userDropdowns.salesOptions || [];
  const { logScheduleChange } = useScheduleChangeLog();

  // SaveBanner state
  const [showSaveBanner, setShowSaveBanner] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  // Local state
  const [filters, setFilters] = useState({
    departments: {
      engineering: true,
      fabrication: true,
      controlPanel: true, 
      pumps: true,
      assembly: true,
      test: true,
      northPrairie: true,
      shipping: true,
      portalDetails: true
    },
    dueDatesOnly: false,
    softwareView: false, // Add new software view filter
    controlPanelView: false,
    northPrairieView: false,
    internationalShippingOnly: false,
    // Add new filter state including notice filters
    additionalFilters: {
      projectManager: 'all',
      salesRep: 'all',
      engineer: 'all',
      stationType: [],
      jobType: [],
      tbdOnly: false,
      moveRequestOnly: false,
      witnessTestOnly: false,
      longBuildOnly: false,
      openChangeOrderTasksOnly: false,
      depositOnly: false
    }  });
  const [weeksToShow, setWeeksToShow] = useState(999);
  
  // Calculate number of weeks needed based on weeksToShow setting
  const numberOfWeeksToCalculate = useMemo(() => {
    if (weeksToShow === 999) {
      return 52;
    } else {
      return Math.max(weeksToShow, 12);
    }
  }, [weeksToShow]);
    // Get shared week calculations with dynamic week count
  const { weekColumns } = useWeekCalculations(numberOfWeeksToCalculate);
  
  // Helper function for notifications
  const showSaveNotificationMessage = useCallback((message, type = "info") => {
    setSaveStatus(message);
    setShowSaveBanner(true);
  }, []);
  
  const [editingCell, setEditingCell] = useState({ jobId: null, field: null });
  const [editValue, setEditValue] = useState("");
  const [highlightedRow, setHighlightedRow] = useState({ id: null, week: null });
    // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [showMOSearchModal, setShowMOSearchModal] = useState(false);
  const [moJobData, setMoJobData] = useState(null);
  const [showEnhancedExportModal, setShowEnhancedExportModal] = useState(false);
  
  // Alerts modal state
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [alertsJob, setAlertsJob] = useState(null);
  
  // Accounting modal state
  const [showAccountingModal, setShowAccountingModal] = useState(false);
  const [accountingJob, setAccountingJob] = useState(null);
  
  // Ref for print functionality
  const printRef = useRef(null);
  
  // Create modal handlers
  const { handleOpenModal, handleCloseModal, handleSaveChanges } = createEditJobHandlers(
    setSelectedJob, 
    setShowModal, 
    hasEditPermission
  );
  
  const { handleOpenNewJobModal, handleCloseNewJobModal } = createNewJobHandlers(
    setShowNewJobModal, 
    setMoJobData, 
    hasEditPermission
  );
  
  const { handleOpenMOSearchModal, handleCloseMOSearchModal, handleSelectMO } = createMOSearchHandlers(
    setShowMOSearchModal, 
    setMoJobData, 
    setShowNewJobModal, 
    hasEditPermission
  );
  
  // Single handler for export/print button
  const handleExportPrintClick = () => {
    setShowEnhancedExportModal(true);
  };
    // Handle filter toggle
  const handleFilterToggle = useCallback((filterName) => {
    setFilters(prev => ({
      ...prev,
      departments: {
        ...prev.departments,
        [filterName]: !prev.departments[filterName]
      }
    }));
  }, []);
    // Process data - use filteredJobs if provided, otherwise use jobScheduleData
  const dataToProcess = useMemo(() => (filteredJobs || jobScheduleData).map((job) => ({
    ...job,
    SoldToShipTo: [...shipToRecords(job.SoldToShipTo ?? job.soldToShipTo), ...shipToRecords(soldToShipToByProject[job.ID ?? job.ProjectID]?.data)],
  })), [filteredJobs, jobScheduleData, soldToShipToByProject]);
    // Apply all additional filters
  const filteredDataToProcess = useMemo(() => {
    let filtered = dataToProcess;
    const selectedStationTypes = expandLegacyStationTypeValues(
      normalizeMultiSelectFilter(filters.additionalFilters.stationType)
    );
    const selectedJobTypes = normalizeMultiSelectFilter(filters.additionalFilters.jobType);
    const selectedEngineer = filters.additionalFilters.engineer || 'all';
    
    // Apply project manager filter
    if (filters.additionalFilters.projectManager !== 'all') {
      filtered = filtered.filter(job => 
        job.ProjectManager === filters.additionalFilters.projectManager
      );
    }
    
    // Apply sales rep filter
    if (filters.additionalFilters.salesRep !== 'all') {
      filtered = filtered.filter(job => 
        job.SalesRep === filters.additionalFilters.salesRep
      );
    }

    // Apply engineer filter across all engineer assignment fields
    if (selectedEngineer !== 'all') {
      filtered = filtered.filter(job => 
        job.MechanicalEngineer === selectedEngineer ||
        job.ElectricalEngineer === selectedEngineer ||
        job.ControlsEngineer === selectedEngineer
      );
    }
    
    // Apply station type filter
    if (selectedStationTypes.length > 0) {
      filtered = filtered.filter(job => 
        selectedStationTypes.includes(getStationType(job))
      );
    }
    
    // Apply job type filter based on JobNumber
    if (selectedJobTypes.length > 0) {
      filtered = filtered.filter(job => {
        const jobType = getJobTypeFromJobNumber(job.JobNumber);
        if (!jobType) return false;
        return selectedJobTypes.includes(jobType);
      });
    }
    
    // Apply software view filter - filter out jobs without software type
    if (filters.softwareView) {
      filtered = filtered.filter((job) => isSoftwareViewJob(job));
    }

    if (filters.controlPanelView) {
      filtered = filtered.filter((job) => isControlPanelViewJob(job));
    }

    if (filters.northPrairieView) {
      filtered = filtered.filter((job) => isNorthPrairieViewJob(job));
    }

    if (filters.internationalShippingOnly) {
      filtered = filtered.filter((job) => isInternationalShippingJob(job, soldToShipToByProject));
    }
      // Apply notice filters - only filter if any notice filter is active
    const hasActiveNoticeFilters = (
      filters.additionalFilters.tbdOnly ||
      filters.additionalFilters.moveRequestOnly ||
      filters.additionalFilters.witnessTestOnly ||
      filters.additionalFilters.longBuildOnly ||
      filters.additionalFilters.openChangeOrderTasksOnly ||
      filters.additionalFilters.depositOnly
    );
    
    if (hasActiveNoticeFilters) {
      filtered = filtered.filter(job => {
        const noticeData = getJobNoticeData(job, pumpDetails);
        
        // Check if job matches any active notice filter (OR logic)
        const matchesFilters = [];
        
        if (filters.additionalFilters.tbdOnly) {
          matchesFilters.push(hasTbd(job.ID));
        }
        
        if (filters.additionalFilters.moveRequestOnly) {
          matchesFilters.push(noticeData.notices.moveRequest?.hasNotice || false);
        }
        
        if (filters.additionalFilters.witnessTestOnly) {
          matchesFilters.push(noticeData.notices.witnessTest?.hasNotice || false);
        }
        
        if (filters.additionalFilters.longBuildOnly) {
          matchesFilters.push(noticeData.notices.longBuild?.hasNotice || false);
        }

        if (filters.additionalFilters.openChangeOrderTasksOnly) {
          matchesFilters.push(hasIncompleteTasks(job.ID));
        }

        if (filters.additionalFilters.depositOnly) {
          const paymentTerms = typeof job.PaymentTerms === 'string' ? job.PaymentTerms.trim().toUpperCase() : '';
          matchesFilters.push(paymentTerms === 'DP' || paymentTerms === 'PBS');
        }
        
        // Return true if job matches any active filter
        return matchesFilters.some(Boolean);
      });
    }
    
    return filtered;
  }, [dataToProcess, filters.additionalFilters, filters.softwareView, filters.controlPanelView, filters.northPrairieView, filters.internationalShippingOnly, soldToShipToByProject, pumpDetails, hasTbd, hasIncompleteTasks]);
  
  // Detect if search is active by checking if filteredJobs prop is provided
  const isSearchActive = filteredJobs !== null;
  
  const {
    formattedJobs,
    groupedJobs,
    sortedWeeks
  } = useMemo(() => {
    return createDataProcessingFunctions(
      filteredDataToProcess, // Use filtered data here
      "", // No search term needed since filtering is done at parent level
      weekColumns,
      weeksToShow,
      isSearchActive, // Pass the search active indicator
      filters.softwareView, // Pass the software view flag for sorting
      filters.controlPanelView,
      filters.northPrairieView
    );
  }, [filteredDataToProcess, weekColumns, weeksToShow, isSearchActive, filters.softwareView, filters.controlPanelView, filters.northPrairieView]);
  
  const handleRowClick = (job) => {
    setHighlightedRow({ 
      id: job.ID, 
      week: job.ScheduleWeek || "Unscheduled"
    });
  };
  // Create cell edit handlers
  const {
    handleCellDoubleClick,
    renderEditableCell
  } = createCellEditHandlers(
    hasEditPermission,
    roles,
    setEditingCell,
    editingCell,
    setEditValue,
    editValue,
    formattedJobs,
    apiUrls,
    logScheduleChange,
    dispatch,
    updateJob,
    setSaveStatus,
    setShowSaveBanner,
    mechOptions,
    elecOptions,
    controlOptions,
    salesOptions, // Pass salesOptions for Sales Rep dropdown
    // Add new parameters for alerts functionality
    mappedUserRoles,
    pumpDetails,
    setShowAlertsModal,
    setAlertsJob,
    // Add accounting modal parameters
    setShowAccountingModal,
    setAccountingJob,
    // Add filters parameter for move request restrictions
    filters
  );

  // Enhanced export handler for the modal
  const handleEnhancedExport = (sectionSelections) => {
    if (sectionSelections.format === 'print') {
      printTableOptimized(
        sectionSelections,
        sortedWeeks,
        weekColumns,
        groupedJobs,
        pumpDetails,
        setSaveStatus,
        setShowSaveBanner
      );
    } else if (sectionSelections.format === 'pdf') {
      exportToPDF(
        sectionSelections,
        sortedWeeks,
        weekColumns,
        groupedJobs,
        setSaveStatus,
        setShowSaveBanner
      );
    } else {
      exportToExcelWithFormatting(
        sectionSelections,
        sortedWeeks,
        weekColumns,
        groupedJobs,
        setSaveStatus,
        setShowSaveBanner
      );
    }
  };

  if (loading) return <div className="loading-indicator">Loading job schedule...</div>;
  if (error) return <div className="error-message">Error: {error}</div>;
  
  return (
    <>      
      {/* Add print wrapper */}
      <div className="job-schedule-print-container">
        <div className="print-only print-header" style={{ display: 'none' }}>
          Job Schedule Report
        </div>
        <div className="print-only print-date" style={{ display: 'none' }}>
          {new Date().toLocaleDateString()}
        </div>
        {/* Scrollable table container */}
        <div className="table-wrapper" ref={printRef}>
          {/* Controls section - now inside scrollable area */}
          <JobScheduleControls
            handleExportPrintClick={handleExportPrintClick}
            handleOpenMOSearchModal={handleOpenMOSearchModal}
            handleOpenNewJobModal={handleOpenNewJobModal}
            hasEditAccess={hasEditPermission}
            userRoles={mappedUserRoles}
            filters={filters}
            handleFilterToggle={handleFilterToggle}
            setFilters={setFilters}
            weeksToShow={weeksToShow}
            setWeeksToShow={setWeeksToShow}
            dataToProcess={dataToProcess}
            getStationType={getStationType}
            showSaveNotificationMessage={showSaveNotificationMessage}
          />
          <div className="schedule-table-viewport schedule-desktop-table-viewport">
            <Table striped bordered className={`job-schedule-table ${!showActionColumn ? 'no-action-column' : ''} ${filters.softwareView ? 'software-view' : ''} ${filters.controlPanelView ? 'control-panel-view' : ''} ${filters.northPrairieView ? 'north-prairie-view' : ''}`}>
            <JobScheduleTableHeader filters={filters} showActionColumn={showActionColumn} />
            <tbody>
              {filters.softwareView ? (
                // Software view: show all jobs in a simple list without week grouping
                formattedJobs.map((job, index) => (
                  <JobScheduleRow
                    key={`${job.ID}-${job.JobNumber}`}
                    job={job}
                    index={index}
                    handleRowClick={handleRowClick}
                    highlightedRow={highlightedRow}
                    handleOpenModal={handleOpenModal}
                    handleCellDoubleClick={handleCellDoubleClick}
                    renderEditableCell={renderEditableCell}
                    hasTbd={hasTbd}
                    hasIncompleteTasks={hasIncompleteTasks}
                    filters={filters}
                    weekColumns={weekColumns}
                    showActionColumn={showActionColumn}
                    pumpDetails={pumpDetails}
                  />
                ))
              ) : (
                // Regular view: show jobs grouped by week
                sortedWeeks.map((week) => {
                  const dateRange = getWeekDateRange(week, weekColumns, moment);
                  const weekJobs = groupedJobs[week] || [];
                  const formattedTotal = calculateWeekTotalDollarValue(weekJobs, formatCurrency);
                  
                  return (
                    <React.Fragment key={`week-${week}`}>
                      <tr className="week-separator print-week-separator" style={{ pageBreakInside: 'avoid' }}>
                        <td 
                          colSpan="100%" 
                          className="week-header print-week-header"
                          style={{ 
                            backgroundColor: '#0055a5',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '12pt',
                            padding: '8px'
                          }}
                        >
                          <span className="week-header-content">
                            {week === "Unscheduled"
                              ? (roles.Admin || roles.Scheduling || roles.AppsManager || roles.EngineerManager
                                  ? `${week} : Total Value: ${formattedTotal}`
                                  : `${week}`)
                              : (roles.Admin || roles.Scheduling || roles.AppsManager || roles.EngineerManager
                                  ? `${week} ${dateRange} : Total Value: ${formattedTotal}`
                                  : `${week} ${dateRange}`)}
                          </span>
                        </td>
                      </tr>

                      {/* Job rows for this week */}
                      {weekJobs.map((job, index) => (
                        <JobScheduleRow
                          key={`${job.ID}-${job.JobNumber}`}
                          job={job}
                          index={index}
                          handleRowClick={handleRowClick}
                          highlightedRow={highlightedRow}
                          handleOpenModal={handleOpenModal}
                          handleCellDoubleClick={handleCellDoubleClick}
                          renderEditableCell={renderEditableCell}
                          hasTbd={hasTbd}
                          hasIncompleteTasks={hasIncompleteTasks}
                          filters={filters}
                          weekColumns={weekColumns}
                          showActionColumn={showActionColumn}
                          pumpDetails={pumpDetails}
                        />
                      ))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            </Table>
          </div>

          <div className="schedule-compact-job-list">
            <div className="schedule-compact-readonly-note">
              <i className="bi bi-lock me-2" aria-hidden="true"></i>
              Read-only schedule. Swipe the header or any detail row to review dates and departments.
            </div>

            {filters.softwareView ? (
              <CompactJobScheduleWeek
                title="Software Schedule"
                jobs={formattedJobs}
                filters={filters}
                weekColumns={weekColumns}
                pumpDetails={pumpDetails}
                hasTbd={hasTbd}
                hasIncompleteTasks={hasIncompleteTasks}
              />
            ) : (
              sortedWeeks.map((week) => {
                const dateRange = getWeekDateRange(week, weekColumns, moment);
                const weekJobs = groupedJobs[week] || [];
                const formattedTotal = calculateWeekTotalDollarValue(weekJobs, formatCurrency);
                const weekLabel = week === "Unscheduled"
                  ? (roles.Admin || roles.Scheduling || roles.AppsManager || roles.EngineerManager
                      ? `${week} : Total Value: ${formattedTotal}`
                      : `${week}`)
                  : (roles.Admin || roles.Scheduling || roles.AppsManager || roles.EngineerManager
                      ? `${week} ${dateRange} : Total Value: ${formattedTotal}`
                      : `${week} ${dateRange}`);

                return (
                  <CompactJobScheduleWeek
                    key={`compact-week-${week}`}
                    title={weekLabel}
                    jobs={weekJobs}
                    filters={filters}
                    weekColumns={weekColumns}
                    pumpDetails={pumpDetails}
                    hasTbd={hasTbd}
                    hasIncompleteTasks={hasIncompleteTasks}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
      
      {/* Modals */}
      {showModal && selectedJob && (
        <Modal 
          className="job-schedule-modal"
          show={showModal} 
          onHide={createModalCloseHandler(handleCloseModal)} 
          size="xl"
        >
          <EditJobModal
            selectedJob={selectedJob}
            setSelectedJob={setSelectedJob}
            handleCloseModal={handleCloseModal}
            handleSaveChanges={handleSaveChanges}
            canEdit={hasEditPermission}
          />
        </Modal>
      )}
      
      {showNewJobModal && (
        <Modal 
          className="job-schedule-modal"
          show={showNewJobModal} 
          onHide={createModalCloseHandler(handleCloseNewJobModal)} 
          size="xl"
        >
          <NewJobModal handleCloseModal={handleCloseNewJobModal} moJobData={moJobData} canEdit={hasEditPermission} userRoles={mappedUserRoles} />
        </Modal>
      )}
      
      <MOSearchModal 
        show={showMOSearchModal} 
        onHide={handleCloseMOSearchModal} 
        onSelectMO={handleSelectMO} 
      />
      
      {/* Enhanced export modal - single modal for all export types */}      <EnhancedExportModal
        show={showEnhancedExportModal}
        onHide={() => setShowEnhancedExportModal(false)}
        onExport={handleEnhancedExport}
        filters={filters}
        mode="export"
      />
      
      {/* Alerts Modal for non-admin users */}
      <AlertsModal
        show={showAlertsModal}
        onHide={() => setShowAlertsModal(false)}
        job={alertsJob}
        pumpDetails={pumpDetails}
      />
      
      {/* Accounting Modal for accounting users */}
      <AccountingModal
        show={showAccountingModal}
        onHide={() => setShowAccountingModal(false)}
        job={accountingJob}
      />
      
      <SaveBanner showNotification={showSaveBanner} saveStatus={saveStatus} />
      
      {/* Add print-only styles */}
      <style jsx>{`
        @media print {
          .print-only {
            display: block !important;
          }
        }
      `}</style>
    </>
  );
};

export default EnhancedJobScheduleTable;
